/**
 * Stop a run in flight (RFC 0269).
 *
 * The composer's stop button. Flips the run to `cancelled` ON THE LOG, which is
 * the only thing every surface can agree on: the tab that pressed stop, the
 * phone that is watching, and the worker that is mid-turn all see the same
 * entry.
 *
 * Cancellation is COOPERATIVE, and it matters that this is honest. Writing
 * `cancelled` does not itself kill anything — it asks. A worker draining the
 * run observes the status and abandons its turn; a harness worker additionally
 * interrupts the harness process. So the UI returns to idle immediately, while
 * the actual agent stops a moment later, and a worker that has already gone
 * away never stops at all (its run is simply no longer anybody's problem).
 *
 * That is the right trade for a distributed log: the alternative, blocking the
 * UI until some possibly-absent worker acknowledges, would hang the composer
 * whenever the worker is the thing that broke.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { BranchLog } from '../../log/branch-log'
import { decodeLogDoc } from '../../log/codec'
import { projectActiveRuns } from '../../log/projection-run-state'
import type { EntryId } from '../../log/types'

/**
 * Cancel the session's active run. Returns the cancelled run's id, or null when
 * nothing was in flight (pressing stop twice, or after it already finished, is
 * a no-op rather than an error).
 */
export async function cancelRun(
  repo: Repo,
  sessionId: string,
  opts: { runId?: EntryId } = {},
): Promise<EntryId | null> {
  const handle = await repo.find(sessionId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()

  const current = decodeLogDoc(handle.doc() as Record<string, unknown>)
  // Cancel EVERY active run unless one is named. A session can hold more than
  // one (a follow-up sent while a run is in flight), and stopping only the
  // newest left the one actually burning tokens running — Stop looked broken.
  const targets = opts.runId
    ? [opts.runId]
    : projectActiveRuns(current).map((r) => r.runId)
  if (targets.length === 0) return null

  // MUST go through BranchLog.mutate, not a hand-rolled handle.change. The
  // on-doc entry is ENCODED: its envelope key is `body`, and scalars like
  // `type` are Automerge ImmutableStrings, so reading `payload.type === 'run'`
  // off the raw draft silently never matches and the write is skipped with no
  // error. mutate decodes, applies, and re-encodes.
  const log = new BranchLog(handle as never)
  let cancelled: EntryId | null = null
  for (const runId of targets) {
    log.mutate(runId, (e) => {
      if (e.payload.type !== 'run') return
      const run = e.payload.payload
      // Only an IN-FLIGHT run can be cancelled. Re-cancelling a finished run
      // would rewrite history and make a completed turn look abandoned.
      if (
        run.status !== 'queued' &&
        run.status !== 'running' &&
        run.status !== 'waiting_for_tools'
      ) {
        return
      }
      run.status = 'cancelled'
      run.completedAt = Date.now()
      cancelled = runId
    })
  }

  if (!cancelled) return null

  // Close the run's OPEN inference step too. Cancelling the run alone leaves
  // the step at `streaming` forever, and a dangling step is what makes a
  // stopped session still look like it is working — observed in the wild:
  // `RUN status=cancelled` next to `STEP status=streaming`. The run is the
  // unit the composer reads, but the step is the unit that says "tokens are
  // arriving", so both have to end for the session to actually look stopped.
  const after = decodeLogDoc(handle.doc() as Record<string, unknown>)
  for (const entry of Object.values(after.entries)) {
    const p = entry.payload
    if (p.type !== 'inference_step') continue
    if (!targets.includes(p.payload.runId)) continue
    if (
      p.payload.status !== 'streaming' &&
      p.payload.status !== 'waiting_for_tools'
    ) {
      continue
    }
    log.mutate(entry.id, (e) => {
      if (e.payload.type !== 'inference_step') return
      e.payload.payload.status = 'cancelled'
    })
  }
  return cancelled
}
